import { memo } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LiteralInput from './components/LiteralInput';
import ResolverPicker from './components/ResolverPicker';
import type { VersionParameter, ResolverEntry } from './types';
import type { BindingDraft, BindingEditorType } from './useBindingEditor';

const BINDING_LABELS: Record<BindingEditorType, string> = {
  literal: 'Literal',
  resolver: 'Resolver',
  'operator-input': 'Operator input',
  'deployment-output': 'Deployment output',
};

interface Props {
  param: VersionParameter;
  draft: BindingDraft;
  resolvers: ResolverEntry[];
  resolversLoading: boolean;
  disabled: boolean;
  onTypeChange: (key: string, type: BindingEditorType) => void;
  onLiteralChange: (key: string, value: string) => void;
  onResolverChange: (key: string, resolverId: string) => void;
  onRequiredChange: (key: string, required: boolean) => void;
  onDeploymentOutputChange: (key: string, field: 'source_component' | 'output_key', value: string) => void;
}

const BindingRowEditor = memo<Props>(({
  param,
  draft,
  resolvers,
  resolversLoading,
  disabled,
  onTypeChange,
  onLiteralChange,
  onResolverChange,
  onRequiredChange,
  onDeploymentOutputChange,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '180px 200px 1fr 120px',
      gap: 1.5,
      alignItems: 'start',
      py: 1.5,
    }}
  >
    {/* CFN key */}
    <Box>
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.8rem', wordBreak: 'break-all' }}>
        {param.cfn_key}
      </Typography>
      {param.has_default && (
        <Typography variant="caption" color="text.secondary">has default</Typography>
      )}
    </Box>

    {/* Type picker */}
    <FormControl>
      <FormLabel sx={{ fontSize: '0.75rem' }}>Type</FormLabel>
      <RadioGroup
        value={draft.type}
        onChange={(e) => onTypeChange(param.cfn_key, e.target.value as BindingEditorType)}
      >
        {(Object.keys(BINDING_LABELS) as BindingEditorType[]).map((t) => (
          <FormControlLabel
            key={t}
            value={t}
            control={<Radio size="small" sx={{ py: 0.25 }} />}
            label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{BINDING_LABELS[t]}</Typography>}
            disabled={disabled}
            sx={{ my: 0 }}
          />
        ))}
      </RadioGroup>
    </FormControl>

    {/* Value editor */}
    <Box>
      {draft.type === 'literal' && (
        <LiteralInput
          value={draft.value}
          onChange={(v) => onLiteralChange(param.cfn_key, v)}
          allowedValues={param.allowed_values}
          allowedPattern={param.allowed_pattern}
          disabled={disabled}
        />
      )}
      {draft.type === 'resolver' && (
        <ResolverPicker
          resolvers={resolvers}
          value={draft.resolver_id || null}
          onChange={(id) => onResolverChange(param.cfn_key, id)}
          disabled={disabled}
          loading={resolversLoading}
        />
      )}
      {draft.type === 'operator-input' && (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={draft.required}
              onChange={(e) => onRequiredChange(param.cfn_key, e.target.checked)}
              disabled={disabled}
            />
          }
          label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Required</Typography>}
        />
      )}
      {draft.type === 'deployment-output' && (
        <Stack spacing={1}>
          <TextField
            size="small"
            label="Source component"
            value={draft.source_component}
            onChange={(e) => onDeploymentOutputChange(param.cfn_key, 'source_component', e.target.value)}
            disabled={disabled}
            placeholder="e.g. networking-baseline"
            sx={{ '& input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
          />
          <TextField
            size="small"
            label="Output key"
            value={draft.output_key}
            onChange={(e) => onDeploymentOutputChange(param.cfn_key, 'output_key', e.target.value)}
            disabled={disabled}
            placeholder="e.g. VpcId"
            sx={{ '& input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
          />
        </Stack>
      )}
    </Box>

    {/* Constraints */}
    <Stack spacing={0.5}>
      {param.allowed_values.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          values: {param.allowed_values.join(', ')}
        </Typography>
      )}
      {param.allowed_pattern && (
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
          /{param.allowed_pattern}/
        </Typography>
      )}
    </Stack>
  </Box>
));

BindingRowEditor.displayName = 'BindingRowEditor';
export default BindingRowEditor;
